import {
  Accordion,
  AccordionRow,
  ExternalTextLink,
  normalizedAccordionId,
  TextLink,
} from "@naf/teamscheme";
import queryString from "query-string";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { TITLE_POSTFIX, useDocumentTitle } from "../shared/DocumentTitle";
import { EVENTS } from "../tracking/EVENTS";
import { useTracking } from "../tracking/TrackingContext";
import FormGroup from "../shared/forms/FormGroup";
import Card from "../shared/layout/Card";
import {
  FilterField,
  NoResultsPlaceholder,
  search,
  useDebouncedQuery,
} from "../shared/search/Filter";
import { normalizeComponentTree } from "../shared/search/normalizeComponentTree";
import StaticHeroPage from "../shared/static-pages/StaticHeroPage";
import { AboutContent } from "../shared/static-pages/StaticPage";
import { getRelativePath } from "../routing/AppRoutes";
import { AppPage } from "../routing/AppPage";
import MITT_NAF_URL from "../membership/MittNafExternalUrl";
import { externalLinkEvent } from "../tracking/externalLinkEvent";

const CUSTOMER_SERVICE_URL = "https://www.naf.no/kundeservice/kontakt-oss";
const CUSTOMER_SERVICE_EMAIL = "mailto:kundeservice@naf.no";

interface FaqItem {
  question: string;
  answer: JSX.Element;
  top?: boolean;
}

interface FaqSection {
  title: string;
  items: FaqItem[];
}
const sections: FaqSection[] = [
  {
    title: "Generelt om skademelding",
    items: [
      {
        top: true,
        question: "Hva er en skademelding?",
        answer: (
          <span>
            Skademeldingen er et skjema som skal fylles ut etter en kollisjon,
            og er et viktig dokument ved vurdering av ansvar etter en
            bilkollisjon. Ved hjelp av skademeldingen kan forsikringsselskapet
            finne ut av hva som skjedde og overføre ansvaret på ansvarlig part.
          </span>
        ),
      },
      {
        top: true,
        question: "Hvorfor bør jeg skrive skademelding?",
        answer: (
          <span>
            Det er anbefalt å skrive skademelding i fellesskap umiddelbart etter
            uhellet. Da har dere hendelsen friskt i minnet, og kan dokumentere
            fra skadestedet. Ved hjelp av skademeldingen kan
            forsikringsselskapet finne ut av hva som skjedde og overføre
            ansvaret på ansvarlig part.
          </span>
        ),
      },
      {
        question: "Er det lovpålagt å skrive skademelding etter en kollisjon?",
        answer: (
          <span>
            Dere er ikke forpliktet til å fylle ut en felles skademelding. Men
            det lønner seg å fylle ut den ut, aller helst i fellesskap på
            stedet, for raskere saksbehandling hos forsikringsselskapet.
          </span>
        ),
      },
      {
        question: "Finnes NAF Digital skademelding på engelsk?",
        answer: (
          <span>
            NAF Digital skademelding kommer kun i norsk utgave. Men
            skademeldingens oppsett, nummerering av felt og innhold er etter
            europeisk standard og lik på alle språk. Har du kollidert med en
            utenlandsk bil eller sjåfør, eller er i utlandet kan dere trygt
            fylle ut skjemaet i fellesskap selv om dere ikke nødvendigvis
            forstår hverandre.
          </span>
        ),
      },
      {
        question:
          "Blir skademeldingen sendt direkte til mitt forsikringsselskap når vi har signert?",
        answer: (
          <span>
            Forsikringstaker for kjøretøyet er selv ansvarlig for at utfylt og
            signert skademelding og vedlegg blir sendt til forsikringsselskapet.
            Etter skademeldingen er fylt ut og signert av begge parter får man
            bekreftelse tilsendt på e-post. Skademeldingen og eventuelle vedlegg
            kan lastes ned fra bekreftelsessiden.
          </span>
        ),
      },
      {
        question:
          "Jeg har kollidert med en trafikant eller en annens eiendom som ikke er et motorisert kjøretøy, skal jeg fortsatt fylle ut skademeldingsskjemaet?",
        answer: (
          <span>
            Skademeldingsskjema bør alltid fylles ut når du kolliderer med
            andre, eller en annens eiendom. Også når det forårsaker skader på
            noe annet enn et motorisert kjøretøy f.eks. Sykkel eller
            sparkesykkel. Har det skjedd materielle skader eller personskader
            skal også dette noteres og meldes i fra til forsikringsselskapet
            slik at ansvarsfordelingen kan gå riktig for seg.
            <br />
            <br />
            Husk at personskader også skal meldes til politiet.{" "}
          </span>
        ),
      },
    ],
  },
  {
    title: "Utfylling av skademelding",
    items: [
      {
        question: "Hvor nøye må vi være med utfyllingen?",
        answer: (
          <span>
            Vær ærlige og nøye med utfyllingen. Dersom ukorrekte opplysninger
            blir gitt til forsikringsselskapet om hendelsen kan det i verste
            fall føre til at forsikringsselskapet avslår hele saken.
          </span>
        ),
      },
      {
        top: true,
        question: "Motparten ønsker ikke å samarbeide, hva gjør jeg?",
        answer: (
          <span>
            Hvis motparten ikke ønsker eller har mulighet til å samarbeide bør
            du fylle ut skademelding uansett. Signer og send til ditt
            forsikringsselskap. Pass på å notere motpartens navn, telefonnummer,
            bilens reg.nr, og en erkjennelse av at uhellet har funnet sted.
          </span>
        ),
      },
      {
        top: true,
        question:
          "Hva gjør jeg hvis jeg ikke har informasjon om forsikringen tilgjengelig?",
        answer: (
          <span>
            Hvis du ikke har opplysninger om kjøretøyets forsikring
            tilgjengelig, kan du trygt gå videre i skjemaet uten å fylle ut
            denne informasjonen. Dette kan føre til noe lenger behandlingstid av
            saken, og kreve mer korrespondanse mellom deg og saksbehandler.
          </span>
        ),
      },
      {
        question: "Må jeg tegne skissen?",
        answer: (
          <span>
            Skissen (tegningen) på side 1 av skademeldingen spiller en sentral
            rolle ved ansvarsvurderingen, men bilder fra skadestedet kan være
            vel så god dokumentasjon. Alternativt kan dere også velge å tegne en
            skisse på papir som dere tar bilde av og laster opp som vedlegg.
          </span>
        ),
      },
      {
        question: "Kan jeg endre det jeg har lagt inn?",
        answer: (
          <span>
            Ja, du kan gå tilbake ved å trykke «Forrige» nederst til venstre.
            Dette er mulig inntil dere har signert. Forhåndsvisningen oppdateres
            automatisk når du gjør endringer.
          </span>
        ),
      },
    ],
  },
  {
    title: "Inviter motpart",
    items: [
      {
        question:
          "Hva gjør jeg når motparten ikke mottar SMS med invitasjon til utfylling?",
        answer: (
          <span>
            Sjekk at mottakers inntastede mobilnummer riktig og prøv på nytt.
          </span>
        ),
      },
    ],
  },
  {
    title: "Signering",
    items: [
      {
        question: "Hva er InkSign?",
        answer: (
          <span>
            InkSign er den enkleste metoden for å signerer dokumentene. Partene
            bruker fingeren for å signere på skjermen på smarttelefon, eller
            musepekeren på MAC eller PC. Dette er en god løsning ved fysisk
            tilstedeværelse av begge parter hvor man i tillegg kan gjøre en
            sjekk om signaturen matcher motparten sin ID som førerkort/pass.
          </span>
        ),
      },
      {
        question:
          "Jeg venter på motparten sin signatur. Kan jeg lukke nettleseren?",
        answer: (
          <span>
            Ja, du kan lukke nettleseren. For å finne tilbake til skjemaet
            bruker du den personlige lenken du fikk tilsendt på SMS eller ved å
            logge inn.
          </span>
        ),
      },
      {
        question:
          "Det står at jeg venter på signatur fra motparten. Hva gjør jeg?",
        answer: (
          <span>
            Du må be motparten signere. Motpart gjør dette ved hjelp av sin
            personlige lenke som ble sendt på SMS.
          </span>
        ),
      },
      {
        question:
          "Vi oppdaget en feil i skademeldingen etter at en av oss har signert, hva gjør vi?",
        answer: (
          <span>
            Så lenge ikke begge har signert skademeldingen kan dere avbryte
            signeringen for å åpne skademeldingen for endringer igjen.
            Signaturen blir da forkastet og dere kan prøve igjen. Hvis begge har
            signert er det ikke mulig å gjøre endringer lenger.
          </span>
        ),
      },
      {
        question:
          "Er det mulig å endre skademeldingen etter at den er signert?",
        answer: <span>Nei.</span>,
      },
    ],
  },
  {
    title: "Oppbevaring",
    items: [
      {
        question: "Hvor lenge lagres skademeldingen?",
        answer: (
          <span>
            Skademeldingen lagres hos NAF i 2 år fra datoen den blir opprettet.
          </span>
        ),
      },
      {
        question: "Hvor finner jeg skademeldingen?",
        answer: (
          <span>
            Du kommer til en bekreftelsesside når begge parter har signert. Her
            kan du laste ned skademeldingen og eventuelle vedlegg. Du kan nå
            denne siden med den personlige lenken du fikk på SMS eller ved å
            logge inn på{" "}
            <ExternalTextLink
              href={MITT_NAF_URL}
              trackEvent={externalLinkEvent(MITT_NAF_URL)}
            >
              Mitt NAF
            </ExternalTextLink>
            . Du får også en e-post med skademeldingen vedlagt.
          </span>
        ),
      },
      {
        question: "Jeg finner ikke en tidligere skademelding.",
        answer: (
          <div>
            <p>
              Sjekk at du har logget inn med korrekte opplysninger. Dine
              skademeldinger vil listes opp på startsiden.
            </p>
            <p>
              Dersom du fortsatt ikke finner skademeldingen du leter etter,
              vennligst kontakt oss på{" "}
              <TextLink
                href={CUSTOMER_SERVICE_EMAIL}
                trackEvent={externalLinkEvent(CUSTOMER_SERVICE_EMAIL)}
              >
                kundeservice@naf.no
              </TextLink>{" "}
              eller bruk{" "}
              <ExternalTextLink
                href={CUSTOMER_SERVICE_URL}
                trackEvent={EVENTS.FAQ.EMAIL_CUSTOMER_SERVICE}
              >
                kontaktskjemaet
              </ExternalTextLink>
              .
            </p>
          </div>
        ),
      },
    ],
  },
];

const FaqWrapper = styled.div`
  section {
    margin-bottom: 3em;
  }
  section h2 {
    ${({ theme }) => theme.fontStyle.headers.header2}
  }
`;

const Answer = styled.div`
  ${({ theme }) => theme.fontStyle.bodyText.bodyText}
  margin-top: 0;
`;

export function TopFaq() {
  const tracking = useTracking();
  const items = sections.flatMap(({ items }) => items.filter((i) => i.top));

  const [openedId, setOpenedId] = useState(null);

  return (
    <FaqWrapper>
      <Accordion>
        <section>
          {items.map((item, index) => (
            <AccordionRow
              id={`${index}`}
              title={item.question}
              key={`top/${item.question}`}
              open={openedId === index}
              onOpen={() => {
                if (openedId !== index) {
                  setOpenedId(null);
                  tracking.event({
                    ...EVENTS.LANDING_PAGE.OPEN_QUESTION,
                    label: item.question,
                  });
                }
              }}
            >
              <Answer>{item.answer}</Answer>
            </AccordionRow>
          ))}
        </section>
      </Accordion>
    </FaqWrapper>
  );
}

interface FaqProps {
  sections: FaqSection[];
  openAll?: boolean;
  openedId?: string;
  onOpen: (id: string) => void;
  query?: string;
}

function Faq({
  sections,
  openAll,
  openedId: initialOpenedId,
  onOpen,
  query,
}: FaqProps) {
  const tracking = useTracking();
  const titles = useRef<HTMLHeadingElement[]>([]);
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const timeout = setTimeout(() => {
        const match = titles.current.filter((x) => `#${x.id}` === hash);
        if (match.length) {
          match[0].scrollIntoView();
        }
      });

      return () => clearTimeout(timeout);
    }
  }, [hash]);

  const [openedId, setOpenedId] = useState<string | null>(
    initialOpenedId || null,
  );

  useEffect(() => {
    if (query) setOpenedId(null);
  }, [query]);

  return (
    <FaqWrapper>
      <Accordion>
        {sections.map((section, i) => {
          const id = normalizedAccordionId(
            normalizeComponentTree(section.title),
          );
          return (
            <section key={`section-${id}`}>
              <h2>{section.title}</h2>
              {section.items.map((item, j) => {
                const id = normalizedAccordionId(
                  normalizeComponentTree(item.question),
                );
                return (
                  <AccordionRow
                    id={id}
                    title={item.question}
                    key={id}
                    open={openAll || openedId === id}
                    titleRef={(x: HTMLHeadingElement | null) => {
                      if (x) titles.current[i * sections.length + j] = x;
                    }}
                    onOpen={() => {
                      if (openedId !== id) {
                        setOpenedId(null);
                        onOpen(id);
                        tracking.event({
                          ...EVENTS.FAQ.OPEN_QUESTION,
                          label: item.question,
                        });
                      }
                    }}
                  >
                    <Answer>{item.answer}</Answer>
                  </AccordionRow>
                );
              })}
            </section>
          );
        })}
      </Accordion>
    </FaqWrapper>
  );
}

export function FaqPageContent() {
  const location = useLocation();
  const { query, clear, inputProps } = useDebouncedQuery({
    queryEvent: EVENTS.FAQ.FILTER,
    queryString:
      (location.search
        ? (queryString.parse(location.search).q as string)
        : null) || "",
  });

  const [matchedSections, setMatchedSections] = useState(sections);

  useEffect(() => {
    const newSections = !query
      ? sections
      : sections
          .map((section) => {
            const items = section.items
              .map((item) => {
                const questionMatch = search(item.question, query);
                const answerMatch = search(item.answer, query);
                return {
                  ...item,
                  questionMatch:
                    questionMatch.weight !== undefined
                      ? questionMatch.result
                      : undefined,
                  answerMatch:
                    answerMatch.weight !== undefined
                      ? answerMatch.result
                      : undefined,
                };
              })
              .filter(
                ({ questionMatch, answerMatch }) =>
                  questionMatch || answerMatch,
              )
              .map(({ ...item }) => ({
                ...item,
                question: item.questionMatch || item.question,
                answer: item.answerMatch || item.answer,
              }));

            if (!items.length) return null;

            return {
              ...section,
              items,
            };
          })
          .filter((s) => !!s);
    setMatchedSections(newSections as FaqSection[]);
  }, [query]);

  return (
    <>
      <FormGroup>
        <FilterField label="Søk på siden" inputProps={inputProps} />
      </FormGroup>
      <Faq
        query={query || undefined}
        sections={matchedSections}
        openAll={!!query}
        openedId={location.hash ? location.hash.replace(/^#/, "") : undefined}
        onOpen={(id) => {
          if (!query) {
            window.history.replaceState(
              null,
              document.title,
              document.location.hash.length
                ? document.location.href.replace(
                    document.location.hash,
                    `#${id}`,
                  )
                : `${document.location.href}#${id}`,
            );
          }
        }}
      />
      {query && !matchedSections.length ? (
        <NoResultsPlaceholder query={query} clear={clear} />
      ) : null}
    </>
  );
}

export function FaqPage() {
  const title = "Vanlige spørsmål";

  useDocumentTitle(`${title} ${TITLE_POSTFIX}`);

  return (
    <StaticHeroPage
      title={title}
      returnTo={getRelativePath(AppPage.LandingPage)}
    >
      <Card>
        <AboutContent>
          <FaqPageContent />
        </AboutContent>
      </Card>
    </StaticHeroPage>
  );
}
